import './App.css';
import React, { useEffect } from 'react';
import {Routes, Route ,useLocation, useNavigate} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Home from './pages/Home';
import Login from './pages/Login';
import VerificationPage from './pages/Verification';
import FranchiseeSelection from './pages/FranchiseeSelection';
import ProtectedRoute from './components/protectedRoute';
import NotFound from './pages/NotFound';
import TWSFranchisee from './pages/TWSFranchisee';
import Analytics from './pages/Analytics';
import UserList from './pages/UserList';
import Version from './pages/Version';
import Eod from './pages/EOD';
import Clients from './pages/Clients';
import { trackEvent } from './utilities/analytics';
import { useUser } from './context/UserContext';
import { useSelectedFranchisee } from './context/SelectedFranchiseeContext';
import isTokenExpired from './utilities/checkTokenExpiration';

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { selectedFranchisee, updateSelectedFranchisee, clearSelectedFranchisee } = useSelectedFranchisee();

  // Handle token expiration and page view tracking
  useEffect(() => {
    // Only track page view if user is logged in and token is valid
    if(user && user.id && !isTokenExpired()){
      (async () => {
        try {
          await trackEvent(
            user.id,
            'Page View',
            null, // No feature name for a generic page view
            { path: location.pathname }
          );
        } catch (error) {
          console.error('Error tracking page view:', error);
        }
      })();
    }
  },[location, user]);

  // Setup a token expiration check that runs periodically
  useEffect(() => {
    // Only check token on routes that aren't public
    if (location.pathname !== '/magic-link-request' && 
        !location.pathname.startsWith('/auth/login/')) {
      // Check token immediately
      if (isTokenExpired()) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('sessionId');
        localStorage.removeItem('viewedPages');
        localStorage.removeItem('selectedClient');
        localStorage.removeItem('selectedFranchisee');
        clearSelectedFranchisee();
        navigate('/magic-link-request');
      }

      // Set up periodic check
      const tokenCheckInterval = setInterval(() => {
        if (isTokenExpired()) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('sessionId');
          localStorage.removeItem('viewedPages');
          localStorage.removeItem('selectedClient');
          localStorage.removeItem('selectedFranchisee');
          clearSelectedFranchisee();
          navigate('/magic-link-request');
          clearInterval(tokenCheckInterval);
        }
      }, 60000); // Check every minute
      
      // Clean up
      return () => clearInterval(tokenCheckInterval);
    }
  }, [location.pathname, navigate]);

  // Check if user needs to select a franchisee
  useEffect(() => {
    // Only check for franchisee selection on protected routes
    const protectedRoutes = ['/', '/eod', '/tws_franchisee', '/clients', '/users', '/analytics', '/version'];
    const isProtectedRoute = protectedRoutes.includes(location.pathname);

    console.log('Logged In User:', user);
    
    if (isProtectedRoute && user && user.id && !isTokenExpired()) {
      // Check if user is an agent with multiple franchisees
      const hasMultipleFranchisees = user.franchisees && user.franchisees.length > 1;
      const hasSingleFranchisee = user.franchisees && user.franchisees.length === 1;
      
      // If user has multiple franchisees and no franchisee is selected, redirect to selection
      if (hasMultipleFranchisees && !selectedFranchisee) {
        navigate('/franchisee-selection');
        return;
      }
      
      // If user has only one franchisee and no franchisee is selected, auto-select it and redirect to home
      if (hasSingleFranchisee && !selectedFranchisee) {
        const singleFranchisee = user.franchisees[0];
        console.log('Selected Franchisee:', singleFranchisee);
        updateSelectedFranchisee(singleFranchisee);
        
        // If we're on franchisee-selection page, redirect to home
        if (location.pathname === '/franchisee-selection') {
          navigate('/');
        }
        return;
      }
      
    }
  }, [location.pathname, user, selectedFranchisee, navigate]);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <Routes location={location} key={location.key}>
        {/* Protected route for authenticated users, if the token expires it will redirect to magiclink request page  */}
        <Route path='/' element={<ProtectedRoute element={Home}/>} />
        <Route path='/eod' element={<ProtectedRoute element={Eod}/>} />
        <Route path='/tws_franchisee' element={<ProtectedRoute element={TWSFranchisee} requiredRole={"admin"} />} />
        <Route path='/clients' element={<ProtectedRoute element={Clients} requiredRole={"admin"} />} />
        <Route path='/users' element={<ProtectedRoute element={UserList} requiredRole={"admin"}/>} />
        <Route path='/analytics' element={<ProtectedRoute element={Analytics} requiredRole={"admin"} />} />
        <Route path='/version' element={<ProtectedRoute element={Version}/>} />
        <Route path='/franchisee-selection' element={<ProtectedRoute element={FranchiseeSelection}/>} />

        {/* Public Routes  */}
        <Route path='/magic-link-request' element={<Login />} />
        <Route path='/auth/login/:token' element={<VerificationPage/>} />

        {/* 404 Routes  */}
        <Route path='/404' element={ <NotFound/> } />
        <Route path="*" element={<NotFound/>} />
      </Routes>
    </>
  );
}

export default App;
